'use client';
import React, { useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import Link from 'next/link';

const serviceGroups = [
  {
    id: 'yemeni_clearance',
    label: 'موافقة أمنية — للمواطنين',
    icon: '🛡️',
    color: 'border-primary bg-primary/5',
    activeColor: 'border-primary bg-primary text-white',
  },
  {
    id: 'yemen_visa',
    label: 'تأشيرة دخول — للزوار',
    icon: '📎',
    color: 'border-accent bg-accent/5',
    activeColor: 'border-accent bg-accent text-white',
  },
  {
    id: 'passport',
    label: 'جواز سفر',
    icon: '📖',
    color: 'border-purple-400 bg-purple-50',
    activeColor: 'border-purple-600 bg-purple-600 text-white',
  },
  {
    id: 'consular',
    label: 'خدمة قنصلية',
    icon: '🏢',
    color: 'border-green-400 bg-green-50',
    activeColor: 'border-green-600 bg-green-600 text-white',
  },
  {
    id: 'authentication',
    label: 'تصديق وثائق',
    icon: '✅',
    color: 'border-orange-400 bg-orange-50',
    activeColor: 'border-orange-600 bg-orange-600 text-white',
  },
  {
    id: 'other',
    label: 'خدمات أخرى',
    icon: '📝',
    color: 'border-gray-300 bg-gray-50',
    activeColor: 'border-gray-600 bg-gray-600 text-white',
  },
];

const formFields: Record<string, Array<{ name: string; label: string; type: string; required?: boolean; options?: string[] }>> = {
  yemeni_clearance: [
    { name: 'fullName', label: 'الاسم الكامل', type: 'text', required: true },
    { name: 'nationalId', label: 'رقم الهوية الوطنية', type: 'text', required: true },
    { name: 'passportNumber', label: 'رقم جواز السفر', type: 'text', required: true },
    { name: 'destination', label: 'الدولة المقصدة', type: 'select', required: true, options: ['مصر', 'الأردن'] },
    { name: 'travelDate', label: 'تاريخ السفر المتوقع', type: 'date', required: true },
    { name: 'purpose', label: 'غرض السفر', type: 'select', required: true, options: ['سياحة', 'علاج', 'عمل', 'دراسة', 'زيارة عائلية', 'أخرى'] },
    { name: 'phone', label: 'رقم الهاتف', type: 'tel', required: true },
    { name: 'email', label: 'البريد الإلكتروني', type: 'email', required: true },
  ],
  yemen_visa: [
    { name: 'fullName', label: 'الاسم الكامل (Latin)', type: 'text', required: true },
    { name: 'nationality', label: 'الجنسية', type: 'text', required: true },
    { name: 'passportNumber', label: 'رقم جواز السفر', type: 'text', required: true },
    { name: 'passportExpiry', label: 'تاريخ انتهاء الجواز', type: 'date', required: true },
    { name: 'arrivalDate', label: 'تاريخ الوصول المتوقع', type: 'date', required: true },
    { name: 'visaType', label: 'نوع التأشيرة', type: 'select', required: true, options: ['سياحية', 'أعمال', 'عائلية', 'عبور', 'دبلوماسية'] },
    { name: 'email', label: 'البريد الإلكتروني', type: 'email', required: true },
    { name: 'phone', label: 'رقم الهاتف', type: 'tel', required: true },
  ],
  passport: [
    { name: 'fullName', label: 'الاسم الكامل', type: 'text', required: true },
    { name: 'nationalId', label: 'رقم الهوية', type: 'text', required: true },
    { name: 'currentPassport', label: 'رقم الجواز الحالي', type: 'text' },
    { name: 'requestType', label: 'نوع الطلب', type: 'select', required: true, options: ['تجديد', 'بدل فاقد', 'بدل تالف', 'تمديد صلاحية'] },
    { name: 'phone', label: 'رقم الهاتف', type: 'tel', required: true },
    { name: 'email', label: 'البريد الإلكتروني', type: 'email', required: true },
  ],
  consular: [
    { name: 'fullName', label: 'الاسم الكامل', type: 'text', required: true },
    { name: 'nationalId', label: 'رقم الهوية', type: 'text', required: true },
    { name: 'serviceType', label: 'نوع الخدمة', type: 'select', required: true, options: ['حجز موعد', 'مساعدة طارئة', 'وثيقة مرور', 'تسجيل سفر'] },
    { name: 'country', label: 'الدولة الحالية', type: 'text', required: true },
    { name: 'phone', label: 'رقم الهاتف', type: 'tel', required: true },
    { name: 'email', label: 'البريد الإلكتروني', type: 'email', required: true },
    { name: 'notes', label: 'ملاحظات', type: 'textarea' },
  ],
  authentication: [
    { name: 'fullName', label: 'الاسم الكامل', type: 'text', required: true },
    { name: 'docType', label: 'نوع الوثيقة', type: 'select', required: true, options: ['شخصية', 'علمية', 'تجارية', 'مؤسسية'] },
    { name: 'destinationCountry', label: 'الدولة المستهدفة', type: 'text', required: true },
    { name: 'phone', label: 'رقم الهاتف', type: 'tel', required: true },
    { name: 'email', label: 'البريد الإلكتروني', type: 'email', required: true },
  ],
  other: [
    { name: 'fullName', label: 'الاسم الكامل', type: 'text', required: true },
    { name: 'serviceDescription', label: 'وصف الخدمة المطلوبة', type: 'textarea', required: true },
    { name: 'phone', label: 'رقم الهاتف', type: 'tel', required: true },
    { name: 'email', label: 'البريد الإلكتروني', type: 'email', required: true },
  ],
};

export default function ApplyPage() {
  const [selectedService, setSelectedService] = useState('yemeni_clearance');
  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);

  const currentFields = formFields[selectedService] || formFields.other;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else {
      setSubmitted(true);
    }
  };

  const handleFileAdd = () => {
    setUploadedFiles((prev) => [...prev, `مستند_${prev.length + 1}.pdf`]);
  };

  const appNumber = `APP-${Math.floor(10000000 + Math.random() * 90000000)}`;

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col font-tajawal" dir="rtl">
        <Navbar />
        <main className="flex-1 bg-background flex items-center justify-center py-12 px-4">
          <div className="w-full max-w-lg text-center">
            <div className="bg-white rounded-2xl border border-border shadow-gov-lg p-10">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">تم تقديم طلبك بنجاح</h2>
              <p className="text-muted-foreground text-sm mb-6">سيتم مراجعة طلبك خلال 3-5 أيام عمل</p>
              <div className="bg-primary/5 rounded-xl border border-primary/20 p-4 mb-6">
                <div className="text-xs text-muted-foreground mb-1">رقم طلبك</div>
                <div className="text-xl font-bold text-primary font-mono" dir="ltr">{appNumber}</div>
                <div className="text-xs text-muted-foreground mt-1">احتفظ بهذا الرقم لمتابعة طلبك</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link href="/track" className="flex-1">
                  <button className="gov-btn-primary w-full justify-center">تتبع الطلب</button>
                </Link>
                <Link href="/" className="flex-1">
                  <button className="gov-btn-outline w-full justify-center">الرئيسية</button>
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col font-tajawal" dir="rtl">
      <Navbar />
      <main className="flex-1 bg-background">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-accent py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">تقديم طلب جديد</h1>
            <p className="text-white/70 text-sm">اختر نوع الخدمة واملأ البيانات المطلوبة</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {[
              { n: 1, label: 'اختيار الخدمة' },
              { n: 2, label: 'بيانات الطلب' },
              { n: 3, label: 'رفع المستندات' },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      step >= s.n ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step > s.n ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : s.n}
                  </div>
                  <span className={`text-xs hidden sm:block ${step >= s.n ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                    {s.label}
                  </span>
                </div>
                {i < 2 && <div className={`w-16 sm:w-24 h-0.5 mb-4 ${step > s.n ? 'bg-primary' : 'bg-border'}`}></div>}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Service Selection */}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-bold text-foreground mb-5">اختر نوع الخدمة</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {serviceGroups.map((sg) => (
                  <button
                    key={sg.id}
                    onClick={() => setSelectedService(sg.id)}
                    className={`p-5 rounded-xl border-2 text-right transition-all ${
                      selectedService === sg.id ? sg.activeColor : sg.color + ' hover:shadow-gov'
                    }`}
                  >
                    <div className="text-3xl mb-2">{sg.icon}</div>
                    <div className={`font-semibold text-sm ${selectedService === sg.id ? 'text-white' : 'text-foreground'}`}>
                      {sg.label}
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(2)} className="gov-btn-primary">
                التالي
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
          )}

          {/* Step 2: Form */}
          {step === 2 && (
            <form onSubmit={handleSubmit}>
              <div className="bg-white rounded-2xl border border-border shadow-gov p-8">
                <h2 className="text-lg font-bold text-foreground mb-6">بيانات الطلب</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {currentFields.map((field) => (
                    <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : ''}>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          required={field.required}
                          className="input-gov"
                          value={formValues[field.name] || ''}
                          onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        >
                          <option value="">اختر...</option>
                          {field.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          required={field.required}
                          rows={3}
                          className="input-gov resize-none"
                          value={formValues[field.name] || ''}
                          onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        />
                      ) : (
                        <input
                          type={field.type}
                          required={field.required}
                          className="input-gov"
                          dir={['email', 'tel', 'text'].includes(field.type) && field.name.includes('passport') ? 'ltr' : undefined}
                          value={formValues[field.name] || ''}
                          onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(1)} className="gov-btn-outline">رجوع</button>
                <button type="submit" className="gov-btn-primary">
                  التالي: رفع المستندات
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Documents */}
          {step === 3 && (
            <div>
              <div className="bg-white rounded-2xl border border-border shadow-gov p-8">
                <h2 className="text-lg font-bold text-foreground mb-2">رفع المستندات</h2>
                <p className="text-sm text-muted-foreground mb-6">ارفع المستندات المطلوبة (صورة جواز السفر، صورة شخصية، وثائق داعمة)</p>

                {/* Upload Area */}
                <div
                  onClick={handleFileAdd}
                  className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-all"
                >
                  <svg className="w-10 h-10 text-muted-foreground mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <p className="text-sm font-medium text-foreground">اضغط لرفع ملف</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG — حجم أقصى 5MB</p>
                </div>

                {/* Uploaded Files */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {uploadedFiles.map((file, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-green-800 flex-1">{file}</span>
                        <button
                          onClick={() => setUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-red-400 hover:text-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(2)} className="gov-btn-outline">رجوع</button>
                <button onClick={() => setSubmitted(true)} className="gov-btn-primary">
                  إرسال الطلب النهائي
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
